// GENERAL
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
// API
import {
	updateApprovalDecision,
	makeApprovalDecision,
	updateApprovalWorkflow,
} from "../../../view/api/OrderViewApi";

// HOOKS
import { useToaster } from "../../../../../hooks/useToasterContext";
import useUserStore from "../../../../../stores/useUserStore";

// TYPES
import { ApprovalDecision, SalesOrder } from "../../../types";
import { UpdateApprovalWorkflowPayload } from "../../../../../globalTypes";
/// UTILS
import handleFormatApprovalWorkflowPayload from "../helpers/handleFormatApprovalWorkflowPayload";

export const useApprovalDecisionsViewModel = ({
	approvalDecisions,
	decisionType,
	fields,
	activeUuid,
	updateRowData,
	onClose,
	parentModalClose,
}: {
	approvalDecisions: ApprovalDecision;
	decisionType: string;
	fields: any;
	activeUuid: string;
	updateRowData?: (uuid: string, key: string, newValue: string) => void;
	onClose?: () => void;
	parentModalClose?: () => void;
}): {
	isLoadingCreateDecision: boolean;
	createApprovalDecisionMutation: any;
} => {
	const queryClient = useQueryClient();
	const { user } = useUserStore();
	const { addToaster } = useToaster();

	//// TEXT FROM FIELDS /////
	const successToasterText = fields?.successToasterMessage?.label || "";
	const errorToasterText = fields?.errorToasterMessage?.label || "";

	const createApprovalDecisionMutation = useMutation({
		mutationFn: async (formData: Record<string, any>) => {
			if (
				decisionType === "approve" &&
				approvalDecisions.currentStage.ApprovalDecisions.uuid === null
			) {
				const result = await makeApprovalDecision(
					approvalDecisions.currentStage,
					user.uuid,
					true,
					formData.reason,
				);
				return result;
			}

			if (
				decisionType === "deny" &&
				approvalDecisions.currentStage.ApprovalDecisions.uuid === null
			) {
				const result = await makeApprovalDecision(
					approvalDecisions.currentStage,
					user.uuid,
					false,
					formData.reason,
				);

				return result;
			}

			if (
				decisionType === "approve" &&
				approvalDecisions.currentStage.ApprovalDecisions.uuid
			) {
				const result = await updateApprovalDecision(
					approvalDecisions.currentStage,
					user.uuid,
					true,
					formData.reason,
				);

				return result;
			}

			if (
				decisionType === "deny" &&
				approvalDecisions.currentStage.ApprovalDecisions.uuid
			) {
				const result = await updateApprovalDecision(
					approvalDecisions.currentStage,
					user.uuid,
					false,
					formData.reason,
				);

				return result;
			}

			if (decisionType === "adminOnly") {
				const payloads = handleFormatApprovalWorkflowPayload(
					formData,
					approvalDecisions,
					user.uuid,
				);

				for (const payload of payloads) {
					await updateApprovalWorkflow(
						payload as UpdateApprovalWorkflowPayload,
					);
				}
				return { success: true };
			}

			throw new Error(`Unhandled decisionType: ${decisionType}`);
		},
		onSuccess: async (data) => {
			addToaster({
				isOpen: true,
				message: `${successToasterText}`,
				status: "success",
			});

			// Invalidate queries - use partial key matching to catch all variants
			await queryClient.refetchQueries({
				queryKey: ["currentApprovalStage", activeUuid],
			});

			// Use refetchQueries to actually wait for fresh data
			await queryClient.refetchQueries({
				queryKey: ["orderDetailsResponse", activeUuid],
			});

			// Find the query data with partial key match
			const queriesData = queryClient.getQueriesData<SalesOrder>({
				queryKey: ["orderDetailsResponse", activeUuid],
			});

			const updatedSalesOrderDataWithUpdatedStatus =
				queriesData?.[0]?.[1];

			// Get the updated approval stage data for assigned to info
			const updatedApprovalStageData = queryClient.getQueryData([
				"currentApprovalStage",
				activeUuid,
				user.uuid,
			]) as { stages: any[]; currentStage: any } | undefined;

			// Now update the row with fresh data
			if (updateRowData) {
				// Update status if we have order data
				if (updatedSalesOrderDataWithUpdatedStatus) {
					updateRowData(
						activeUuid,
						"status",
						updatedSalesOrderDataWithUpdatedStatus._status,
					);
				}

				// Update assigned admin and manager if we have approval stage data
				if (updatedApprovalStageData?.stages) {
					// For adminOnly flow, get the assigned user from stage 1 (Admin Review)
					const adminStage = updatedApprovalStageData.stages.find(
						(stage: any) =>
							stage?.ApprovalTemplateStages?.step === "1",
					);

					const assignedAdminName =
						adminStage?.AssignedTo?._name || "";

					updateRowData(
						activeUuid,
						"assigned-admin",
						assignedAdminName,
					);

					// Also update the assigned manager from stage 2 (Manager Review)
					const managerStage = updatedApprovalStageData.stages.find(
						(stage: any) =>
							stage?.ApprovalTemplateStages?.step === "2",
					);

					const assignedManagerName =
						managerStage?.AssignedTo?._name || "";

					updateRowData(
						activeUuid,
						"assigned-manager",
						assignedManagerName,
					);
				}
			}
			!parentModalClose && onClose && onClose();
		},
		onError: (err) => {
			addToaster({
				isOpen: true,
				message: `${errorToasterText}`,
				status: "error",
			});
			!parentModalClose &&  onClose && onClose();
		},
	});

	return {
		isLoadingCreateDecision: createApprovalDecisionMutation.isPending,
		createApprovalDecisionMutation,
	};
};
